import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { RefreshCw, Loader, TrendingUp, TrendingDown } from 'react-feather';

const api = process.env.REACT_APP_API_URL;

/**
 * "Revised ATS score", one click re-scores the GENERATED resume against the
 * job description with the same engine that scored the original, and shows
 * before → after.
 */
export default function RevisedScore({ onMissing }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchScore = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${api}revised-score/`, {}, { withCredentials: true });
      setResult(res.data);
      onMissing?.(Array.isArray(res.data.missing_keywords) ? res.data.missing_keywords : []);
    } catch {
      toast.error('Could not compute the revised score. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!result) {
    return (
      <button
        onClick={fetchScore}
        disabled={loading}
        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-500 disabled:opacity-50 transition text-sm"
      >
        {loading ? <Loader className="animate-spin w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
        {loading ? 'Scoring…' : 'Updated ATS Score'}
      </button>
    );
  }

  const prev = Math.round(result.previous_score ?? 0);
  const now = Math.round(result.score ?? 0);
  const delta = now - prev;
  const up = delta >= 0;

  return (
    <div className="w-full bg-white border border-emerald-200 rounded-2xl p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-6">
        <div className="text-center">
          <p className="text-xs text-gray-400 font-medium mb-1">Before</p>
          <span className="text-2xl font-bold text-gray-400">{prev}%</span>
        </div>
        <span className="text-2xl text-gray-300">→</span>
        <div className="text-center">
          <p className="text-xs text-emerald-600 font-medium mb-1">ATS resume</p>
          <span className="text-3xl font-extrabold text-emerald-600">{now}%</span>
        </div>
        <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {up ? '+' : ''}{delta} pts
        </div>
        <button
          onClick={fetchScore}
          disabled={loading}
          className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition"
          title="Re-score (e.g. after editing with chat)"
        >
          {loading ? <Loader className="animate-spin w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-check
        </button>
      </div>
      {Array.isArray(result.missing_keywords) && result.missing_keywords.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-1.5">Still missing (couldn't be added honestly):</p>
          <div className="flex flex-wrap gap-1.5">
            {result.missing_keywords.map((kw, i) => (
              <span key={i} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{kw}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
